import { useEffect, useState, useCallback, Fragment } from "react";
import { Routes, Route, Navigate, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { TAG_PALETTE } from "@/lib/tagPalette";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { X, Plus, ChevronDown, ChevronUp } from "lucide-react";
import ApplicationsPanel from "./admin/ApplicationsPanel";
import ReferralLeaderboard from "./admin/ReferralLeaderboard";
import DraftsPanel from "./admin/DraftsPanel";
import ContactsPanel from "./admin/ContactsPanel";
import CampaignsPanel from "./admin/CampaignsPanel";
import PartnershipsPanel from "./admin/PartnershipsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatStorage(s) {
    if (!s) return "—";
    const bytes = s.bytes || 0;
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    let size;
    if (gb >= 1) size = `${gb.toFixed(1)} GB`;
    else if (mb >= 1) size = `${mb.toFixed(0)} MB`;
    else size = `${Math.max(kb, 1).toFixed(0)} KB`;
    return `${(s.photos || 0) + (s.videos || 0)} · ${size}`;
}

function Login() {
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/admin/login", { email, password: pw });
            toast.success("Welcome.");
            nav("/admin");
        } catch { toast.error("Invalid credentials"); }
        finally { setBusy(false); }
    };
    return (
        <main className="min-h-screen bg-[color:var(--warm-black)] flex items-center justify-center px-6" data-testid="admin-login">
            <form onSubmit={submit} className="w-full max-w-md border border-[color:var(--espresso)] p-10 space-y-6">
                <div>
                    <p className="eyebrow">Admin</p>
                    <h1 className="font-display text-5xl mt-2">Sign in.</h1>
                </div>
                <input required type="email" className="input-line" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
                <input required type="password" className="input-line" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-login-password" />
                <button type="submit" disabled={busy} className="btn-gold btn-gold-solid w-full justify-center" data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
                <Link to="/" className="text-xs link-underline">← Back to site</Link>
            </form>
        </main>
    );
}

function ProfileEditor({ open, onClose, application, onSaved }) {
    const [bio, setBio] = useState("");
    const [filmingDate, setFilmingDate] = useState("");
    const [headshotUrl, setHeadshotUrl] = useState("");
    const [schedule, setSchedule] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!application) return;
        setBio(application.bio || "");
        setFilmingDate(application.filming_date ? application.filming_date.slice(0, 10) : "");
        setHeadshotUrl(application.headshot_url || (application.photo_keys?.[0] ? `/api/uploads/${application.photo_keys[0]}` : ""));
        setSchedule(
            application.deposit_schedule?.length
                ? application.deposit_schedule
                : [
                    { label: "Deposit", amount_cents: 50000, due_date: "On acceptance", paid: false },
                    { label: "Installment 1", amount_cents: 25000, due_date: "30 days after acceptance", paid: false },
                    { label: "Installment 2", amount_cents: 25000, due_date: "60 days after acceptance", paid: false },
                    { label: "Installment 3", amount_cents: 25000, due_date: "90 days after acceptance", paid: false },
                ]
        );
    }, [application]);

    if (!application) return null;

    const updateItem = (i, key, val) => setSchedule((s) => s.map((it, idx) => {
        if (idx !== i) return it;
        const parsedVal = key === "amount_cents" ? Number(val) : val;
        return { ...it, [key]: parsedVal };
    }));
    const removeItem = (i) => setSchedule((s) => s.filter((_, idx) => idx !== i));
    const addItem = () => setSchedule((s) => [...s, { label: `Installment ${s.length}`, amount_cents: 25000, due_date: "TBD", paid: false }]);

    const save = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/applications/${application.id}/profile`, {
                bio: bio || null,
                filming_date: filmingDate || null,
                headshot_url: headshotUrl || null,
                deposit_schedule: schedule,
            });
            toast.success("Profile saved.");
            onSaved?.();
            onClose();
        } catch { toast.error("Save failed"); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-[color:var(--warm-black)] border-[color:var(--espresso)] max-w-3xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="profile-editor">
                <DialogHeader>
                    <p className="eyebrow">Cast profile</p>
                    <DialogTitle className="font-display text-4xl">{application.first_name} {application.last_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-8 mt-4">
                    <div className="grid md:grid-cols-[140px_1fr] gap-6 items-start">
                        <div>
                            {headshotUrl ? (
                                <img src={headshotUrl.startsWith("http") ? headshotUrl : `${BACKEND_URL}${headshotUrl}`} alt="" className="w-full h-40 object-cover border border-[color:var(--espresso)]" />
                            ) : (
                                <div className="w-full h-40 border border-[color:var(--espresso)] bg-[color:var(--dark-grey)]" />
                            )}
                            {application.photo_keys?.length > 0 && (
                                <div className="mt-2 grid grid-cols-3 gap-1">
                                    {application.photo_keys.map((k) => (
                                        <button key={k} onClick={() => setHeadshotUrl(`/api/uploads/${k}`)} className="border border-[color:var(--espresso)] hover:border-[color:var(--gold)] p-0.5" data-testid={`select-headshot-${k}`}>
                                            <img src={`${BACKEND_URL}/api/uploads/${k}`} alt="" className="w-full h-10 object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="eyebrow">Headshot URL</label>
                                <input className="input-line" value={headshotUrl} onChange={(e) => setHeadshotUrl(e.target.value)} placeholder="/api/uploads/... or https://..." data-testid="profile-headshot-url" />
                            </div>
                            <div>
                                <label className="eyebrow">Filming date</label>
                                <input type="date" className="input-line" value={filmingDate} onChange={(e) => setFilmingDate(e.target.value)} data-testid="profile-filming-date" />
                            </div>
                            <div>
                                <label className="eyebrow">Bio</label>
                                <textarea className="input-line" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A few sentences that will appear on their private page…" data-testid="profile-bio" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <p className="eyebrow">Personalised deposit schedule</p>
                            <button type="button" onClick={addItem} className="btn-gold text-[0.65rem]" data-testid="add-schedule-item"><Plus size={12} /> Add installment</button>
                        </div>
                        <div className="mt-4 space-y-3">
                            {schedule.map((s, i) => (
                                <div key={`${s.label}-${s.due_date}-${i}`} className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-3 items-center" data-testid={`schedule-row-${i}`}>
                                    <input className="input-line" value={s.label} onChange={(e) => updateItem(i, "label", e.target.value)} placeholder="Label" />
                                    <input className="input-line" value={s.due_date} onChange={(e) => updateItem(i, "due_date", e.target.value)} placeholder="Due date" />
                                    <input type="number" className="input-line w-28" value={Math.round(s.amount_cents / 100)} onChange={(e) => updateItem(i, "amount_cents", Number(e.target.value) * 100)} placeholder="$" />
                                    <label className="flex items-center gap-2 text-xs text-[color:var(--cream-muted)]">
                                        <input type="checkbox" checked={s.paid} onChange={(e) => updateItem(i, "paid", e.target.checked)} className="accent-[color:var(--gold)]" />
                                        paid
                                    </label>
                                    <button type="button" onClick={() => removeItem(i)} className="text-[color:var(--cream-muted)] hover:text-[color:var(--wine)]" data-testid={`remove-schedule-${i}`}><X size={16} /></button>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 text-xs text-[color:var(--cream-muted)]">
                            Total: <span className="text-[color:var(--gold)]">${(schedule.reduce((a, b) => a + (b.amount_cents || 0), 0) / 100).toFixed(0)}</span>
                        </p>
                    </div>

                    {application.private_token && (
                        <div className="border-t border-[color:var(--espresso)] pt-4">
                            <p className="eyebrow">Private link</p>
                            <a href={`/apply/private/${application.private_token}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-[color:var(--gold)] text-sm break-all link-underline" data-testid="profile-private-link">
                                {window.location.origin}/apply/private/{application.private_token}
                            </a>
                        </div>
                    )}
                </div>
                <DialogFooter className="mt-6">
                    <button onClick={onClose} className="btn-gold" data-testid="profile-cancel">Cancel</button>
                    <button onClick={save} disabled={busy} className="btn-gold btn-gold-solid" data-testid="profile-save">{busy ? "Saving…" : "Save profile"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Dashboard() {
    const nav = useNavigate();
    const [sum, setSum] = useState(null);
    const [apps, setApps] = useState([]);
    const [orders, setOrders] = useState([]);
    const [subs, setSubs] = useState([]);
    const [partners, setPartners] = useState([]);
    const [msgs, setMsgs] = useState([]);
    const [waitlist, setWaitlist] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [features, setFeatures] = useState([]);
    const [spotlights, setSpotlights] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [selectedAppId, setSelectedAppId] = useState(null);
    const [expandedFeatureId, setExpandedFeatureId] = useState(null);
    const [editingFeature, setEditingFeature] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [s, a, o, u, p, m, w, an, f, sp, dr] = await Promise.all([
                api.get("/admin/summary"),
                api.get("/admin/applications"),
                api.get("/admin/orders"),
                api.get("/admin/subscribers"),
                api.get("/admin/partnerships"),
                api.get("/admin/contact"),
                api.get("/admin/waitlist"),
                api.get("/admin/trailer-analytics"),
                api.get("/admin/features"),
                api.get("/admin/spotlights"),
                api.get("/admin/casting/drafts"),
            ]);
            setSum(s.data); setApps(a.data); setOrders(o.data); setSubs(u.data); setPartners(p.data); setMsgs(m.data); setWaitlist(w.data); setAnalytics(an.data); setFeatures(f.data); setSpotlights(sp.data); setDrafts(dr.data);
        } catch (e) {
            if (e?.response?.status === 401) { nav("/admin/login"); }
        }
    }, [nav]);
    useEffect(() => { refresh(); }, [refresh]);

    const setStatus = async (id, status) => {
        try {
            const { data } = await api.post(`/admin/applications/${id}/status?status=${status}`);
            if (data.private_token) toast.success("Accepted. Cast profile ready to edit.", { duration: 8000 });
            refresh();
        } catch { toast.error("Update failed"); }
    };

    const logout = async () => {
        try { await api.post("/admin/logout"); } catch {
            // Logout endpoint may 401 if session already expired — we clear locally anyway.
        }
        nav("/admin/login");
    };

    if (!sum) return <main className="pt-40 text-center min-h-screen text-[color:var(--cream-muted)]">Loading…</main>;

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-[color:var(--warm-black)] pt-10 pb-20">
            <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
                <div className="flex justify-between items-center border-b border-[color:var(--espresso)] pb-6">
                    <div>
                        <p className="eyebrow">Admin</p>
                        <h1 className="font-serif text-3xl mt-1">Mrs.Black(In)Love</h1>
                    </div>
                    <div className="flex gap-4">
                        <Link to="/" className="btn-gold" data-testid="admin-view-site">View site</Link>
                        <button onClick={logout} className="btn-gold" data-testid="admin-logout">Log out</button>
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-2 md:grid-cols-7 gap-4">
                    {[["Apps", sum.applications], ["Orders", sum.orders], ["Subs", sum.subscribers], ["Waitlist", sum.waitlist ?? 0], ["Partners", sum.partnerships], ["Messages", sum.contact_messages], ["Payments", sum.payments], ["Storage", formatStorage(sum.storage)]].map(([k, v]) => (
                        <div key={k} className="border border-[color:var(--espresso)] p-6">
                            <p className="eyebrow">{k}</p>
                            <p className="font-serif text-4xl text-[color:var(--gold)] mt-2">{v}</p>
                        </div>
                    ))}
                </div>

                <Tabs defaultValue="apps" className="mt-14" data-testid="admin-tabs">
                    <TabsList className="bg-transparent border border-[color:var(--espresso)] rounded-none p-1">
                        <TabsTrigger value="apps" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Applications</TabsTrigger>
                        <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Orders</TabsTrigger>
                        <TabsTrigger value="subs" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Subscribers</TabsTrigger>
                        <TabsTrigger value="waitlist" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Waitlist</TabsTrigger>
                        <TabsTrigger value="partners" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Partnerships</TabsTrigger>
                        <TabsTrigger value="msgs" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Messages</TabsTrigger>
                        <TabsTrigger value="trailer" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Trailer</TabsTrigger>
                        <TabsTrigger value="features" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Features</TabsTrigger>
                        <TabsTrigger value="spotlight" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]">Spotlight</TabsTrigger>
                        <TabsTrigger value="tags" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]" data-testid="tab-tags">Tags</TabsTrigger>
                        <TabsTrigger value="drafts" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]" data-testid="tab-drafts">Drafts <span className="ml-2 opacity-70">{drafts.length}</span></TabsTrigger>
                        <TabsTrigger value="contacts" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]" data-testid="tab-contacts">Contacts</TabsTrigger>
                        <TabsTrigger value="campaigns" className="rounded-none data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--warm-black)]" data-testid="tab-campaigns">Campaigns</TabsTrigger>
                    </TabsList>

                    <TabsContent value="apps" className="mt-8" data-testid="apps-tab">
                        <CallbackSchedulerConfig />
                        <ReferralLeaderboard />
                        <ApplicationsPanel
                            apps={apps}
                            selectedId={selectedAppId}
                            onSelect={setSelectedAppId}
                            onStatus={setStatus}
                            onEdit={setEditing}
                            onRefresh={refresh}
                        />
                    </TabsContent>
                    <TabsContent value="orders" className="mt-8">
                        <table className="w-full text-sm"><thead className="text-[color:var(--gold)] text-left"><tr className="border-b border-[color:var(--espresso)]"><th className="py-3">Event</th><th>Tier</th><th>Qty</th><th>Amount</th><th>Name</th><th>Email</th><th>Status</th></tr></thead>
                            <tbody>{orders.map((o) => (<tr key={o.id} className="border-b border-[color:var(--espresso)]/50"><td className="py-3">{o.event_slug}</td><td>{o.tier_name}</td><td>{o.quantity}</td><td>${(o.amount_cents/100).toFixed(2)}</td><td>{o.name}</td><td>{o.email}</td><td>{o.status}</td></tr>))}</tbody></table>
                    </TabsContent>
                    <TabsContent value="subs" className="mt-8">
                        <table className="w-full text-sm"><thead className="text-[color:var(--gold)] text-left"><tr className="border-b border-[color:var(--espresso)]"><th className="py-3">Email</th><th>Phone</th><th>Interests</th><th>SMS</th></tr></thead>
                            <tbody>{subs.map((s) => (<tr key={s.id} className="border-b border-[color:var(--espresso)]/50"><td className="py-3">{s.email}</td><td>{s.phone || "—"}</td><td>{(s.interests || []).join(", ")}</td><td>{s.sms_consent ? "Yes" : "No"}</td></tr>))}</tbody></table>
                    </TabsContent>
                    <TabsContent value="waitlist" className="mt-8">
                        <table className="w-full text-sm"><thead className="text-[color:var(--gold)] text-left"><tr className="border-b border-[color:var(--espresso)]"><th className="py-3">Event</th><th>Email</th><th>Name</th><th>Phone</th><th>SMS</th><th>Joined</th></tr></thead>
                            <tbody>{waitlist.map((w) => (<tr key={w.id} className="border-b border-[color:var(--espresso)]/50" data-testid={`waitlist-row-${w.id}`}><td className="py-3">{w.event_title || w.event_slug}</td><td>{w.email}</td><td>{w.name || "—"}</td><td>{w.phone || "—"}</td><td>{w.sms_consent ? "Yes" : "No"}</td><td>{new Date(w.created_at).toLocaleDateString()}</td></tr>))}</tbody></table>
                    </TabsContent>
                    <TabsContent value="partners" className="mt-8" data-testid="partners-tab">
                        <PartnershipsPanel partners={partners} onRefresh={refresh} />
                    </TabsContent>
                    <TabsContent value="msgs" className="mt-8">
                        <table className="w-full text-sm"><thead className="text-[color:var(--gold)] text-left"><tr className="border-b border-[color:var(--espresso)]"><th className="py-3">Name</th><th>Email</th><th>Subject</th><th>Message</th></tr></thead>
                            <tbody>{msgs.map((m) => (<tr key={m.id} className="border-b border-[color:var(--espresso)]/50"><td className="py-3">{m.name}</td><td>{m.email}</td><td>{m.subject}</td><td className="max-w-md">{m.message}</td></tr>))}</tbody></table>
                    </TabsContent>
                    <TabsContent value="trailer" className="mt-8" data-testid="trailer-analytics-tab">
                        {analytics && (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                    <div className="border border-[color:var(--espresso)] p-6"><p className="eyebrow">Events</p><p className="font-serif text-4xl text-[color:var(--gold)] mt-2">{analytics.total}</p></div>
                                    <div className="border border-[color:var(--espresso)] p-6"><p className="eyebrow">Clicks</p><p className="font-serif text-4xl text-[color:var(--gold)] mt-2">{analytics.clicks}</p></div>
                                    <div className="border border-[color:var(--espresso)] p-6"><p className="eyebrow">Plays</p><p className="font-serif text-4xl text-[color:var(--gold)] mt-2">{analytics.plays}</p></div>
                                    <div className="border border-[color:var(--espresso)] p-6"><p className="eyebrow">Play rate</p><p className="font-serif text-4xl text-[color:var(--gold)] mt-2">{analytics.play_rate ? Math.round(analytics.play_rate * 100) + "%" : "—"}</p></div>
                                </div>
                                <table className="w-full text-sm">
                                    <thead className="text-[color:var(--gold)] text-left"><tr className="border-b border-[color:var(--espresso)]"><th className="py-3">Source</th><th>Action</th><th>Count</th></tr></thead>
                                    <tbody>{analytics.breakdown.map((r) => (<tr key={`${r.source}-${r.action}`} className="border-b border-[color:var(--espresso)]/50" data-testid={`trailer-row-${r.source}-${r.action}`}><td className="py-3 font-serif">{r.source}</td><td>{r.action}</td><td className="text-[color:var(--gold)]">{r.count}</td></tr>))}</tbody>
                                </table>
                            </>
                        )}
                    </TabsContent>
                    <TabsContent value="features" className="mt-8" data-testid="features-tab">
                        <FeaturesBroadcastBar features={features} onDone={refresh} />
                        <table className="w-full text-sm">
                            <thead className="text-[color:var(--gold)] text-left"><tr className="border-b border-[color:var(--espresso)]"><th className="py-3 w-8"></th><th>Business</th><th>Category</th><th>City</th><th>Contact</th><th>Status</th><th>Actions</th></tr></thead>
                            <tbody>
                                {features.length === 0 && (
                                    <tr><td colSpan={7} className="py-10 text-center text-[color:var(--cream-muted)] italic">No feature applications yet.</td></tr>
                                )}
                                {features.map((f) => {
                                    const open = expandedFeatureId === f.id;
                                    return (
                                        <Fragment key={f.id}>
                                            <tr className="border-b border-[color:var(--espresso)]/50 hover:bg-[color:var(--espresso)]/20" data-testid={`feature-row-${f.id}`}>
                                                <td className="py-3">
                                                    <button
                                                        onClick={() => setExpandedFeatureId(open ? null : f.id)}
                                                        className="text-[color:var(--gold)] hover:text-[color:var(--soft-cream)] p-1"
                                                        aria-label={open ? "Collapse" : "Expand"}
                                                        data-testid={`feature-expand-${f.id}`}
                                                    >{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                                                </td>
                                                <td className="py-3">
                                                    <div className="font-serif text-lg">{f.business_name}</div>
                                                    <div className="text-xs text-[color:var(--cream-muted)]">{f.owner_name}</div>
                                                </td>
                                                <td className="capitalize">{(f.category || "").replace("_", " / ")}</td>
                                                <td>{f.city}</td>
                                                <td>
                                                    <div className="text-xs">{f.email}</div>
                                                    {f.phone && <div className="text-xs text-[color:var(--cream-muted)]">{f.phone}</div>}
                                                </td>
                                                <td>
                                                    <span className="chip active">{f.status}</span>
                                                    {f.is_featured && <span className="ml-2 text-[color:var(--gold)] text-xs">★ Featured</span>}
                                                </td>
                                                <td className="space-x-3">
                                                    <button onClick={() => setEditingFeature(f)} className="text-xs link-underline text-[color:var(--gold)]" data-testid={`feature-edit-${f.id}`}>Edit</button>
                                                    <button onClick={async () => { await api.post(`/admin/features/${f.id}/status?status=approved`); refresh(); }} className="text-xs link-underline">Approve</button>
                                                    <button onClick={async () => { await api.post(`/admin/features/${f.id}/publish`); toast.success("Now featured on homepage"); refresh(); }} className="text-xs link-underline text-[color:var(--gold)]" data-testid={`publish-feature-${f.id}`}>Publish this week</button>
                                                    <button onClick={async () => { await api.post(`/admin/features/${f.id}/status?status=declined`); refresh(); }} className="text-xs link-underline">Decline</button>
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr className="bg-[color:var(--espresso)]/10 border-b border-[color:var(--espresso)]/50" data-testid={`feature-detail-${f.id}`}>
                                                    <td></td>
                                                    <td colSpan={6} className="py-6 pr-6">
                                                        <FeaturePreview feature={f} />
                                                        <div className="grid md:grid-cols-[1fr_180px] gap-6">
                                                            <div className="space-y-5">
                                                                <div>
                                                                    <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-1">About the business</p>
                                                                    <p className="text-sm text-[color:var(--soft-cream)] whitespace-pre-wrap leading-relaxed">{f.description || <em className="text-[color:var(--cream-muted)]">— no description provided —</em>}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-1">Why now?</p>
                                                                    <p className="text-sm text-[color:var(--soft-cream)] whitespace-pre-wrap leading-relaxed">{f.why_featured || <em className="text-[color:var(--cream-muted)]">— not provided —</em>}</p>
                                                                </div>
                                                                <div className="flex flex-wrap gap-4 text-xs">
                                                                    {f.website && <a href={f.website} target="_blank" rel="noopener noreferrer" className="link-underline text-[color:var(--gold)]" data-testid={`feature-website-${f.id}`}>Website ↗</a>}
                                                                    {f.instagram && <a href={`https://instagram.com/${(f.instagram || "").replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="link-underline text-[color:var(--gold)]" data-testid={`feature-ig-${f.id}`}>Instagram: {f.instagram}</a>}
                                                                    <span className="text-[color:var(--cream-muted)]">Submitted {f.created_at ? new Date(f.created_at).toLocaleDateString() : "—"}</span>
                                                                    {f.last_email_template && (
                                                                        <span className="text-[color:var(--cream-muted)] italic">Last email: {f.last_email_template.replace("_", " ")} · {f.last_email_sent_at ? new Date(f.last_email_sent_at).toLocaleDateString() : ""}</span>
                                                                    )}
                                                                </div>
                                                                <FeatureReplyTemplates feature={f} onSent={refresh} />
                                                            </div>
                                                            {f.photo_url ? (
                                                                <a href={f.photo_url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`feature-photo-${f.id}`}>
                                                                    <img src={f.photo_url} alt="submission" className="w-full h-40 object-cover border border-[color:var(--espresso)] hover:opacity-80 transition" />
                                                                    <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] mt-2 text-center">Open full size ↗</p>
                                                                </a>
                                                            ) : (
                                                                <div className="border border-dashed border-[color:var(--espresso)] h-40 flex items-center justify-center text-[0.65rem] uppercase tracking-[0.2em] text-[color:var(--cream-muted)] italic">No photo attached</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </TabsContent>
                    <TabsContent value="spotlight" className="mt-8" data-testid="spotlight-tab">
                        <SpotlightPanel spotlights={spotlights} onCreated={refresh} />
                    </TabsContent>
                    <TabsContent value="tags" className="mt-8" data-testid="tags-tab">
                        <TagsPanel onChanged={refresh} />
                    </TabsContent>
                    <TabsContent value="drafts" className="mt-8" data-testid="drafts-tab">
                        <DraftsPanel drafts={drafts} onChanged={refresh} />
                    </TabsContent>
                    <TabsContent value="contacts" className="mt-8" data-testid="contacts-tab">
                        <ContactsPanel />
                    </TabsContent>
                    <TabsContent value="campaigns" className="mt-8" data-testid="campaigns-tab">
                        <CampaignsPanel />
                    </TabsContent>
                </Tabs>
            </div>

            <ProfileEditor open={!!editing} onClose={() => setEditing(null)} application={editing} onSaved={refresh} />
            <FeatureEditor open={!!editingFeature} feature={editingFeature} onClose={() => setEditingFeature(null)} onSaved={refresh} />
        </main>
    );
}

function FeatureEditor({ open, feature, onClose, onSaved }) {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!feature) { setForm(null); return; }
        setForm({
            business_name: feature.business_name || "",
            owner_name: feature.owner_name || "",
            email: feature.email || "",
            phone: feature.phone || "",
            city: feature.city || "",
            category: feature.category || "other",
            website: feature.website || "",
            instagram: (feature.instagram || "").replace(/^@/, ""),
            description: feature.description || "",
            why_featured: feature.why_featured || "",
            photo_url: feature.photo_url || "",
            extra_photos: Array.isArray(feature.extra_photos) ? feature.extra_photos.join("\n") : "",
        });
    }, [feature]);

    if (!form) return null;

    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const uploadPhoto = async (file, target) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/features/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = `${BACKEND_URL}${r.data.url}`;
            if (target === "hero") {
                setForm((s) => ({ ...s, photo_url: url }));
            } else {
                setForm((s) => {
                    const existing = (s.extra_photos || "").split("\n").map((x) => x.trim()).filter(Boolean);
                    existing.push(url);
                    return { ...s, extra_photos: existing.join("\n") };
                });
            }
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        setBusy(true);
        try {
            const payload = { ...form };
            payload.extra_photos = (payload.extra_photos || "").split("\n").map((x) => x.trim()).filter(Boolean);
            const { data } = await api.patch(`/admin/features/${feature.id}`, payload);
            toast.success("Feature updated");
            onSaved && onSaved();
            onClose();
            return data;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl bg-[color:var(--warm-black)] border border-[color:var(--espresso)] text-[color:var(--soft-cream)] max-h-[90vh] overflow-y-auto" data-testid="feature-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl">Edit feature</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Business name</span>
                            <input className="input-line w-full" value={form.business_name} onChange={set("business_name")} data-testid="edit-business-name" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Owner / contact name</span>
                            <input className="input-line w-full" value={form.owner_name} onChange={set("owner_name")} data-testid="edit-owner-name" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Email</span>
                            <input type="email" className="input-line w-full" value={form.email} onChange={set("email")} data-testid="edit-email" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Phone</span>
                            <input className="input-line w-full" value={form.phone} onChange={set("phone")} data-testid="edit-phone" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">City</span>
                            <input className="input-line w-full" value={form.city} onChange={set("city")} data-testid="edit-city" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Category</span>
                            <select className="input-line w-full" value={form.category} onChange={set("category")} data-testid="edit-category">
                                {["food_drink","beauty","wellness","fashion","art","service","product","other"].map((v) => (
                                    <option key={v} value={v} className="bg-[color:var(--warm-black)]">{v.replace("_"," / ")}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Website</span>
                            <input className="input-line w-full" placeholder="faifquin.com" value={form.website} onChange={set("website")} data-testid="edit-website" />
                            <span className="text-[0.6rem] text-[color:var(--cream-muted)] italic">Missing https:// gets added automatically. `.con` → `.com` is auto-fixed.</span>
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Instagram handle</span>
                            <input className="input-line w-full" placeholder="faifquin" value={form.instagram} onChange={set("instagram")} data-testid="edit-instagram" />
                            <span className="text-[0.6rem] text-[color:var(--cream-muted)] italic">Without the @.</span>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">About the business</span>
                        <textarea rows={4} className="input-line w-full" value={form.description} onChange={set("description")} data-testid="edit-description" />
                    </label>
                    <label className="block">
                        <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Why featured?</span>
                        <textarea rows={2} className="input-line w-full" value={form.why_featured} onChange={set("why_featured")} data-testid="edit-why" />
                    </label>

                    <div className="border-t border-[color:var(--espresso)] pt-4">
                        <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Hero photo</span>
                        <div className="mt-2 flex items-start gap-4">
                            {form.photo_url && (
                                <img src={form.photo_url} alt="hero" className="w-24 h-24 object-cover border border-[color:var(--espresso)]" data-testid="edit-hero-preview" />
                            )}
                            <div className="flex-1 space-y-2">
                                <input className="input-line w-full" placeholder="Paste a direct image URL, or upload below" value={form.photo_url} onChange={set("photo_url")} data-testid="edit-photo-url" />
                                <label className={"inline-flex items-center gap-2 btn-gold cursor-pointer text-xs " + (uploading ? "opacity-40 pointer-events-none" : "")}>
                                    {uploading ? "Uploading…" : "Upload new hero photo"}
                                    <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0], "hero")} data-testid="edit-hero-upload" />
                                </label>
                                <p className="text-[0.6rem] text-[color:var(--cream-muted)] italic">Instagram post URLs won't render — use a direct image link or upload.</p>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-[color:var(--espresso)] pt-4">
                        <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Additional gallery photos (one URL per line)</span>
                        <textarea rows={3} className="input-line w-full font-mono text-xs" placeholder="https://…\nhttps://…" value={form.extra_photos} onChange={set("extra_photos")} data-testid="edit-extra-photos" />
                        <label className={"mt-2 inline-flex items-center gap-2 btn-gold cursor-pointer text-xs " + (uploading ? "opacity-40 pointer-events-none" : "")}>
                            {uploading ? "Uploading…" : "+ Add gallery photo"}
                            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0], "gallery")} data-testid="edit-gallery-upload" />
                        </label>
                    </div>
                </div>
                <DialogFooter className="mt-6 flex gap-3 justify-end">
                    <button onClick={onClose} className="btn-gold text-xs" data-testid="edit-cancel">Cancel</button>
                    <button onClick={save} disabled={busy} className="btn-gold btn-gold-solid text-xs disabled:opacity-40" data-testid="edit-save">{busy ? "Saving…" : "Save changes"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FeaturesBroadcastBar({ features, onDone }) {
    const [busy, setBusy] = useState(false);
    const missing = (features || []).filter((f) => {
        const url = (f.photo_url || "").trim();
        return (f.status === "approved" || f.status === "submitted") && (!url || !(url.startsWith("http://") || url.startsWith("https://")));
    }).length;

    const broadcast = async () => {
        if (missing === 0) return;
        const ok = window.confirm(`Send an upload link to ${missing} applicant${missing === 1 ? "" : "s"} missing a real photo? Each recipient gets a private one-time link.`);
        if (!ok) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/features/broadcast-photo-request");
            toast.success(`Sent ${data.sent} · ${data.failed} failed`);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Broadcast failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-[color:var(--espresso)] p-5" data-testid="features-broadcast-bar">
            <div>
                <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Photo re-upload broadcast</p>
                <p className="text-[0.72rem] text-[color:var(--cream-muted)] mt-1 italic">
                    {missing === 0
                        ? "Every approved feature has a real uploaded photo — nothing to do."
                        : `${missing} approved / submitted feature${missing === 1 ? "" : "s"} still ${missing === 1 ? "has" : "have"} no uploaded photo. One click below emails each of them a private upload link.`}
                </p>
            </div>
            <button
                onClick={broadcast}
                disabled={busy || missing === 0}
                className="btn-gold btn-gold-solid disabled:opacity-40"
                data-testid="broadcast-photo-request"
            >{busy ? "Sending…" : `Ping ${missing} applicant${missing === 1 ? "" : "s"}`}</button>
        </div>
    );
}

function FeaturePreview({ feature }) {
    const igHandle = (feature.instagram || "").replace(/^@/, "");
    const extras = Array.isArray(feature.extra_photos) ? feature.extra_photos.slice(0, 2) : [];
    return (
        <div className="mb-6 border border-[color:var(--gold)]/30 bg-[color:var(--warm-black)] p-5" data-testid={`feature-preview-${feature.id}`}>
            <div className="flex items-baseline justify-between mb-4">
                <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Homepage preview</p>
                <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] italic">exactly how it will render</p>
            </div>
            <div className="grid md:grid-cols-12 gap-6 items-start">
                {feature.photo_url ? (
                    <div className="md:col-span-5 overflow-hidden">
                        <img src={feature.photo_url} alt={feature.business_name} className="w-full h-[260px] object-cover" />
                    </div>
                ) : (
                    <div className="md:col-span-5 h-[260px] border border-dashed border-[color:var(--espresso)] flex items-center justify-center text-[0.65rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] italic">
                        Add a hero photo to preview
                    </div>
                )}
                <div className={feature.photo_url ? "md:col-span-7" : "md:col-span-7"}>
                    <p className="text-[0.65rem] uppercase tracking-[0.28em] text-[color:var(--gold)]">
                        {(feature.category || "").replace("_", " ")}{feature.city ? ` · ${feature.city}` : ""}
                    </p>
                    <h3 className="font-display text-3xl md:text-4xl mt-2 leading-[0.95] text-[color:var(--soft-cream)]">{feature.business_name || <em className="text-[color:var(--cream-muted)]">Business name</em>}</h3>
                    {feature.description && <p className="mt-3 text-sm text-[color:var(--cream-muted)] max-w-lg leading-relaxed line-clamp-3">{feature.description}</p>}
                    <div className="mt-4 flex flex-wrap gap-3 items-center">
                        {igHandle && (
                            <span className="inline-block bg-[color:var(--gold)] text-[color:var(--warm-black)] px-4 py-2 text-[0.62rem] uppercase tracking-[0.22em] font-semibold">Follow @{igHandle}</span>
                        )}
                        {feature.website && (
                            <span className="inline-block border border-[color:var(--gold)] text-[color:var(--gold)] px-4 py-2 text-[0.62rem] uppercase tracking-[0.22em]">Visit website</span>
                        )}
                    </div>
                    {extras.length > 0 && (
                        <div className="mt-5">
                            <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-2">The Work</p>
                            <div className="grid grid-cols-2 gap-2">
                                {extras.map((src, i) => (
                                    <div key={src || `extra-${i}`} className="overflow-hidden">
                                        <img src={src} alt={`extra ${i + 1}`} className="w-full h-[110px] object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CallbackSchedulerConfig() {
    const [url, setUrl] = useState("");
    const [saved, setSaved] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/admin/config/calendly");
                if (alive) {
                    setUrl(data.calendly_url || "");
                    setSaved(data.calendly_url || "");
                }
            } catch {
                // Non-fatal: the input still renders empty and admin can set a fresh URL.
            }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/config/calendly", { calendly_url: url.trim() });
            setSaved(data.calendly_url || "");
            toast.success(data.calendly_url ? "Callback booking link saved" : "Callback booking link cleared");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const dirty = url.trim() !== saved;

    return (
        <div className="mb-8 border border-[color:var(--espresso)] p-5" data-testid="callback-scheduler-config">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                    <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Callback booking link</p>
                    <p className="text-[0.72rem] text-[color:var(--cream-muted)] mt-1 italic">Paste your private Calendly / Cal.com URL. When you flip an applicant to <strong>Callback</strong>, this button lands inside their email so they self-schedule.</p>
                </div>
                <div className="flex flex-1 gap-3 items-center">
                    <input
                        type="url"
                        placeholder="https://calendly.com/mrs-blackinlove/casting-callback"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        disabled={loading}
                        className="input-line flex-1"
                        data-testid="calendly-url-input"
                    />
                    <button
                        onClick={save}
                        disabled={busy || loading || !dirty}
                        className="btn-gold btn-gold-solid disabled:opacity-40"
                        data-testid="calendly-url-save"
                    >{busy ? "Saving…" : "Save"}</button>
                </div>
            </div>
            {saved && !dirty && (
                <p className="mt-3 text-[0.68rem] text-[color:var(--cream-muted)]">
                    Currently live · <a href={saved} target="_blank" rel="noreferrer" className="link-underline text-[color:var(--gold)]" data-testid="calendly-url-preview">{saved}</a>
                </p>
            )}
        </div>
    );
}

function FeatureReplyTemplates({ feature, onSent }) {
    const [busy, setBusy] = useState(null);
    const [showNote, setShowNote] = useState(false);
    const [note, setNote] = useState("");

    const send = async (template) => {
        setBusy(template);
        try {
            await api.post(`/admin/features/${feature.id}/send-email`, {
                template,
                custom_note: note.trim() || null,
            });
            toast.success(`Sent "${template.replace("_", " ")}" email to ${feature.email}`);
            setNote("");
            setShowNote(false);
            onSent && onSent();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Email send failed");
        } finally {
            setBusy(null);
        }
    };

    const sendUploadLink = async () => {
        setBusy("upload_link");
        try {
            await api.post(`/admin/features/${feature.id}/request-photo-reupload`);
            toast.success(`Private upload link sent to ${feature.email}`);
            onSent && onSent();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Send failed");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="mt-5 border-t border-[color:var(--espresso)]/50 pt-4" data-testid={`feature-templates-${feature.id}`}>
            <div className="flex items-center justify-between mb-3">
                <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)]">Reply templates</p>
                <button
                    onClick={() => setShowNote((v) => !v)}
                    className="text-[0.62rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] hover:text-[color:var(--gold)]"
                    data-testid={`feature-toggle-note-${feature.id}`}
                >{showNote ? "Hide personal note" : "+ Add personal note"}</button>
            </div>
            {showNote && (
                <textarea
                    className="input-line w-full mb-3"
                    rows={2}
                    placeholder="Optional — a warm line that appears inside the email body."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid={`feature-note-${feature.id}`}
                />
            )}
            <div className="flex flex-wrap gap-3">
                <button
                    onClick={() => send("approve")}
                    disabled={busy !== null}
                    className="btn-gold btn-gold-solid text-xs disabled:opacity-40"
                    data-testid={`feature-send-approve-${feature.id}`}
                >{busy === "approve" ? "Sending…" : "✓ Approve & email"}</button>
                <button
                    onClick={() => send("request_photo")}
                    disabled={busy !== null}
                    className="btn-gold text-xs disabled:opacity-40"
                    data-testid={`feature-send-request-photo-${feature.id}`}
                >{busy === "request_photo" ? "Sending…" : "Request photo"}</button>
                <button
                    onClick={sendUploadLink}
                    disabled={busy !== null}
                    className="btn-gold text-xs disabled:opacity-40"
                    data-testid={`feature-send-upload-link-${feature.id}`}
                >{busy === "upload_link" ? "Sending…" : "Send upload link"}</button>
                <button
                    onClick={() => send("decline")}
                    disabled={busy !== null}
                    className="text-xs link-underline text-[color:var(--wine)] hover:text-[color:var(--cream-muted)] disabled:opacity-40"
                    data-testid={`feature-send-decline-${feature.id}`}
                >{busy === "decline" ? "Sending…" : "Decline & email"}</button>
            </div>
        </div>
    );
}



function TagsPanel({ onChanged }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // { old_label, new_label, new_color }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/tags");
            setRows(Array.isArray(data) ? data : []);
        } catch { toast.error("Failed to load tags"); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const startEdit = (r) => setEditing({ old_label: r.label, new_label: r.label, new_color: r.color });
    const cancelEdit = () => setEditing(null);
    const saveEdit = async () => {
        if (!editing?.new_label.trim()) return;
        try {
            const { data } = await api.post("/admin/tags/rename", editing);
            toast.success(`Updated ${data.modified} applicant(s)`);
            setEditing(null);
            await load();
            onChanged?.();
        } catch { toast.error("Rename failed"); }
    };
    const removeEverywhere = async (label) => {
        if (!window.confirm(`Delete "${label}" from every applicant? This cannot be undone.`)) return;
        try {
            const { data } = await api.delete(`/admin/tags/${encodeURIComponent(label)}`);
            toast.success(`Removed from ${data.modified} applicant(s)`);
            await load();
            onChanged?.();
        } catch { toast.error("Delete failed"); }
    };

    if (loading) return <p className="text-[color:var(--cream-muted)] text-sm" data-testid="tags-panel-loading">Loading tags…</p>;
    if (rows.length === 0) return (
        <div className="border border-[color:var(--espresso)] p-12 text-center" data-testid="tags-panel-empty">
            <p className="font-serif italic text-[color:var(--cream-muted)]">No tags yet. Add tags from the Applications tab and manage them here.</p>
        </div>
    );

    return (
        <div className="border border-[color:var(--espresso)]" data-testid="tags-panel">
            <div className="px-6 py-4 border-b border-[color:var(--espresso)] flex items-baseline justify-between">
                <p className="eyebrow">All tags · {rows.length}</p>
                <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Rename or recolor here → changes ripple across every applicant</span>
            </div>
            <table className="w-full text-sm">
                <thead className="text-[color:var(--gold)] text-left">
                    <tr className="border-b border-[color:var(--espresso)]">
                        <th className="py-3 px-6">Tag</th>
                        <th className="py-3 px-6">Color</th>
                        <th className="py-3 px-6">Used by</th>
                        <th className="py-3 px-6 text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const isEdit = editing?.old_label === r.label;
                        return (
                            <tr key={r.label} className="border-b border-[color:var(--espresso)]/50" data-testid={`tags-row-${r.label}`}>
                                <td className="py-4 px-6">
                                    {isEdit ? (
                                        <input
                                            className="input-line w-full"
                                            value={editing.new_label}
                                            onChange={(e) => setEditing({ ...editing, new_label: e.target.value })}
                                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                                            autoFocus
                                            data-testid={`tags-rename-input-${r.label}`}
                                        />
                                    ) : (
                                        <span className="text-[0.7rem] uppercase tracking-[0.2em] px-2 py-1 border" style={{ borderColor: r.color, color: r.color }}>{r.label}</span>
                                    )}
                                </td>
                                <td className="py-4 px-6">
                                    {isEdit ? (
                                        <div className="flex gap-1">
                                            {TAG_PALETTE.map((c) => (
                                                <button
                                                    key={c.name}
                                                    onClick={() => setEditing({ ...editing, new_color: c.hex })}
                                                    className={"w-6 h-6 border-2 transition-transform " + (editing.new_color === c.hex ? "scale-125" : "border-transparent")}
                                                    style={{ background: c.hex, borderColor: editing.new_color === c.hex ? "#f7f4f0" : "transparent" }}
                                                    data-testid={`tags-recolor-${r.label}-${c.name}`}
                                                    aria-label={c.name}
                                                    type="button"
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="w-5 h-5" style={{ background: r.color }} />
                                            <span className="text-xs font-mono text-[color:var(--cream-muted)]">{r.color}</span>
                                        </div>
                                    )}
                                </td>
                                <td className="py-4 px-6 font-serif text-lg text-[color:var(--gold)]">{r.count}</td>
                                <td className="py-4 px-6 text-right space-x-3">
                                    {isEdit ? (
                                        <>
                                            <button onClick={saveEdit} className="text-xs link-underline text-[color:var(--gold)]" data-testid={`tags-save-${r.label}`}>Save</button>
                                            <button onClick={cancelEdit} className="text-xs link-underline text-[color:var(--cream-muted)]" data-testid={`tags-cancel-${r.label}`}>Cancel</button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => startEdit(r)} className="text-xs link-underline" data-testid={`tags-edit-${r.label}`}>Rename / recolor</button>
                                            <button onClick={() => removeEverywhere(r.label)} className="text-xs link-underline text-[color:var(--wine)]" data-testid={`tags-delete-${r.label}`}>Delete everywhere</button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


function SpotlightPanel({ spotlights, onCreated }) {
    const [form, setForm] = useState({ name: "", quote: "", photo_url: "", role: "", location: "" });
    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
    const save = async () => {
        if (!form.name || !form.quote || !form.photo_url) { toast.error("Name, quote, and photo URL are required"); return; }
        try {
            const { data } = await api.post("/admin/spotlights", form);
            await api.post(`/admin/spotlights/${data.id}/publish`);
            toast.success("Spotlight published on homepage");
            setForm({ name: "", quote: "", photo_url: "", role: "", location: "" });
            onCreated();
        } catch { toast.error("Could not save spotlight"); }
    };
    const publish = async (id) => {
        try { await api.post(`/admin/spotlights/${id}/publish`); toast.success("Now live on homepage"); onCreated(); }
        catch { toast.error("Failed"); }
    };
    return (
        <div className="grid md:grid-cols-2 gap-10">
            <div className="border border-[color:var(--espresso)] p-8">
                <p className="eyebrow">Add spotlight</p>
                <div className="mt-6 space-y-4">
                    <input className="input-line" placeholder="Guest name" value={form.name} onChange={set("name")} data-testid="spotlight-name" />
                    <textarea className="input-line" placeholder="Their quote in their own words" value={form.quote} onChange={set("quote")} data-testid="spotlight-quote" />
                    <input className="input-line" placeholder="Photo URL" value={form.photo_url} onChange={set("photo_url")} data-testid="spotlight-photo" />
                    <input className="input-line" placeholder="Role (e.g. Sunset in Tuscany guest)" value={form.role} onChange={set("role")} />
                    <input className="input-line" placeholder="Location (e.g. Atlanta, GA)" value={form.location} onChange={set("location")} />
                    <button onClick={save} className="btn-gold btn-gold-solid w-full justify-center" data-testid="spotlight-save">Save & publish</button>
                </div>
            </div>
            <div>
                <p className="eyebrow">Previous spotlights</p>
                <div className="mt-6 space-y-3">
                    {spotlights.map((s) => (
                        <div key={s.id} className={`border p-5 flex items-center gap-4 ${s.is_current ? "border-[color:var(--gold)]" : "border-[color:var(--espresso)]"}`} data-testid={`spotlight-row-${s.id}`}>
                            <img src={s.photo_url} alt={s.name} className="w-16 h-16 object-cover" />
                            <div className="flex-1 min-w-0">
                                <p className="font-serif text-lg truncate">{s.name}</p>
                                <p className="text-xs text-[color:var(--cream-muted)] truncate">"{s.quote}"</p>
                            </div>
                            {s.is_current ? (
                                <span className="text-[color:var(--gold)] text-xs uppercase tracking-[0.24em]">Live</span>
                            ) : (
                                <button onClick={() => publish(s.id)} className="text-xs link-underline text-[color:var(--gold)]">Make current</button>
                            )}
                        </div>
                    ))}
                    {spotlights.length === 0 && <p className="text-sm text-[color:var(--cream-muted)]">Nothing yet — add your first spotlight on the left.</p>}
                </div>
            </div>
        </div>
    );
}

function Protected() {
    const nav = useNavigate();
    const [authed, setAuthed] = useState(null); // null=checking, true=ok, false=no
    useEffect(() => {
        api.get("/admin/me")
            .then(() => setAuthed(true))
            .catch(() => { setAuthed(false); nav("/admin/login", { replace: true }); });
    }, [nav]);
    if (authed === null) return <main className="pt-40 text-center min-h-screen text-[color:var(--cream-muted)]" data-testid="admin-auth-checking">Checking session…</main>;
    return authed ? <Dashboard /> : null;
}

export default function Admin() {
    return (
        <Routes>
            <Route path="login" element={<Login />} />
            <Route path="" element={<Protected />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
    );
}
